import { useCallback } from 'react';
import { useStateManager } from './useStateManager';
import { logger } from '../utils/logger';
import { saveRecentResponse } from '../utils/recentResponseUtils';
import { MessageClassification } from './useMessageRouting';

export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  formData?: { [key: string]: string };
  aiResponseReceivedAt?: Date;
  isRetryable?: boolean;
  classification?: MessageClassification;
  skipIntroAndClosing?: boolean; // Skip intro/closing in parsing - already streamed to user
}

// Export Message type for backward compatibility
export type Message = ChatMessage;

export const useChatMessages = () => {
  const { state, addMessage, updateMessages, isRestoring } = useStateManager();
  
  const messages: ChatMessage[] = state.messages || [];
  // Use the actual isRestoring state from the state manager

  // Check for duplicate messages - use a ref to always get fresh messages
  const isDuplicateMessage = useCallback((newMessage: ChatMessage): boolean => {
    // CRITICAL: Get fresh messages from state, not from closure
    const currentMessages = state.messages || [];
    return currentMessages.some((existing: ChatMessage) => 
      existing.id === newMessage.id ||
      (existing.content === newMessage.content && 
       existing.isUser === newMessage.isUser &&
       Math.abs(new Date(existing.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 1000)
    );
  }, [state.messages]);

  const addUserMessage = useCallback(async (content: string, formData?: { [key: string]: string }): Promise<ChatMessage> => {
    const displayContent = content.trim();
    const newMessage: ChatMessage = {
      id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: displayContent,
      isUser: true,
      timestamp: new Date(),
      formData
    };

    // Check for duplicates
    if (isDuplicateMessage(newMessage)) {
      logger.log('Skipping duplicate user message');
      return newMessage;
    }

    addMessage(newMessage);
    logger.log('💾 USER MESSAGE: Saved to state manager');
    
    // Note: Message is already saved synchronously in useChat.sendMessage before this is called

    return newMessage;
  }, [isDuplicateMessage, addMessage]);

  const addAIMessage = useCallback(async (content: string, aiResponseReceivedAt?: Date, classification?: MessageClassification, skipIntroAndClosing?: boolean): Promise<ChatMessage> => {
    const newMessage: ChatMessage = {
      id: `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content,
      isUser: false,
      timestamp: aiResponseReceivedAt || new Date(), // Use passed timestamp (streamStartTime) or fallback
      aiResponseReceivedAt: aiResponseReceivedAt || new Date(),
      classification,
      skipIntroAndClosing
    };

    // Check for duplicates
    if (isDuplicateMessage(newMessage)) {
      logger.log('Skipping duplicate AI message');
      return newMessage;
    }

    addMessage(newMessage);
    logger.log('💾 AI MESSAGE: Saved to state manager');

    // Save response for Cmd+D shortcut
    saveRecentResponse(content);
    
    // Note: AI message already saved synchronously in useOpenAIResponses after API response

    return newMessage;
  }, [isDuplicateMessage, addMessage]);

  const addCarfaxMessage = useCallback(async (carfaxData: any): Promise<ChatMessage> => {
    const newMessage: ChatMessage = {
      id: `carfax_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: JSON.stringify({
        type: 'carfax_report',
        vehicleData: carfaxData.vehicleData,
        carfaxUrl: carfaxData.carfaxUrl,
        customerName: carfaxData.customerName,
        customerPhone: carfaxData.customerPhone
      }),
      isUser: false,
      timestamp: new Date()
    };

    // Check for duplicates
    if (isDuplicateMessage(newMessage)) {
      logger.log('Skipping duplicate Carfax message');
      return newMessage;
    }

    addMessage(newMessage);
    logger.log('💾 CARFAX MESSAGE: Saved to state manager');

    return newMessage;
  }, [isDuplicateMessage, addMessage]);

  const addErrorMessage = useCallback(async (error: string, isRetryable: boolean = false): Promise<ChatMessage> => {
    const newMessage: ChatMessage = {
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content: `Error: ${error}`,
      isUser: false,
      timestamp: new Date(),
      isRetryable
    };

    // Check for duplicates
    if (isDuplicateMessage(newMessage)) {
      logger.log('Skipping duplicate error message');
      return newMessage;
    }

    addMessage(newMessage);
    logger.log('💾 ERROR MESSAGE: Saved to state manager');

    return newMessage;
  }, [isDuplicateMessage, addMessage]);

  const clearMessages = useCallback(() => {
    updateMessages([]);
    logger.log('🧹 MESSAGES: Cleared from state manager');
    
    // Also clear from sessionStorage + backup localStorage
    try {
      sessionStorage.removeItem('d2c_chat_messages');
      localStorage.removeItem('d2c_chat_messages_backup');
      logger.log('🧹 MESSAGES: Cleared from sessionStorage (+ localStorage backup)');
    } catch (error) {
      console.error('Error clearing messages from storage:', error);
    }
  }, [updateMessages]);

  return {
    messages,
    isRestoring,
    addUserMessage,
    addAIMessage,
    addCarfaxMessage,
    addErrorMessage,
    clearMessages,
  };
};